<template>
  <section class="book-visit-form">
    <h2>Book Video Call</h2>
    <router-link :to="{ name: 'calls' }" class="body-text">
      &laquo; Back to video calls
    </router-link>
    <div class="row mt-3">
      <div class="col-sm-6">
        <b-card>
          <b-alert class="p-1" variant="warning" :show="latestBookingDate !== null">The {{'prison' | term(true)}} has a latest booking date, therefore no dates will show for booking slots after {{latestBookingDate | formatDate}}</b-alert>

          <div>
            <div class="form-group">
              <label class="control-label">{{'prisoner' | term(true)}} Number</label>
              <div class="input-group">
                <input cy="prisoner-number" type="text" class="form-control" v-model="prisonerNumber">
                <div class="input-group-append">
                  <button
                    class="btn btn-primary"
                    v-on:click="searchForContacts"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
            <div class="form-group" v-if="wingEnabled">
              <label class="control-label">Wing</label>
              <select cy="wings"
                :disabled="loadingWings"
                class="form-control"
                v-model="selectedWing"
                v-on:change="selectWing()"
              >
                <option v-for="(wing, idx) in wings" :key="idx" :value="wing">
                  {{wing.name}}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label class="control-label">Visitor</label>
              <select
                cy="visitor"
                :disabled="loadingContacts"
                class="form-control"
                v-model="visitor"
                v-on:change="selectVisitor()"
              >
                <option v-for="(contact, idx) in contacts" :key="idx" :value="contact">{{contact.user.firstname}} {{contact.user.lastname}} is my {{contact.relationship.toLowerCase()}}
                </option>
              </select>
            </div>
            <div class="form-group" v-if="visitor">
              <label class="control-label">Additional Visitors</label>
              <div v-if="additionalVisitors && additionalVisitors.length > 0">
                <b-table
                  class="data-table"
                  cy="add-visitors-table"
                  hover
                  :responsive="true"
                  :items="additionalVisitors"
                  :fields="columns"
                  :current-page="currentPage"
                  :per-page="perPage"
                >
                  <template #cell(checkbox)="data">
                    <button
                      v-if="selectedAdditionalVisitorIds && !selectedAdditionalVisitorIds.includes(data.item.additional_visitor_id)"
                      class="btn btn-success btn-sm"
                      @click="selectAdditionalVisitor(data.item)"
                    >
                      <span class="fa fa-plus"></span>
                    </button>
                    <button
                      v-if="selectedAdditionalVisitorIds && selectedAdditionalVisitorIds.includes(data.item.additional_visitor_id)"
                      class="btn btn-danger btn-sm"
                      @click="deSelectAdditionalVisitor(data.item)"
                    >
                      <span class="fa fa-minus"></span>
                    </button>
                  </template>
                  <template #cell(birthDateAndAge)="data">
                    {{data.item.birthDateAndAge}}
                    <span v-if="isUnder18(data.item) && !data.item.safeguarded_at_prison" class="badge badge-danger badge-pill">UNDER 18</span>
                    <span v-else-if="isUnder18(data.item) && data.item.safeguarded_at_prison" class="badge badge-success badge-pill">UNDER 18</span>
                  </template>
                  <template #cell(actions)="data">
                    <button class="btn btn-primary btn-sm" @click="viewDetails(data.item)">
                      <span class="fas fa-search"></span> View
                    </button>
                  </template>
                </b-table>
                <b-row v-if="additionalVisitors && additionalVisitors.length > perPage">
                  <b-col md="6" class="my-1">
                    <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="my-0"/>
                  </b-col>
                </b-row>
                <div class="row">
                  <div class="col-12">
                    <p>
                      <small>{{selectedAdditionalVisitorIds.length}}/{{MAX_ADDITIONAL_VISITORS}} selected</small>
                    </p>
                  </div>
                </div>
              </div>
              <div v-else>
                <p>
                  <small>None available</small>
                </p>
              </div>
            </div>
            <div class="form-group">
              <label class="control-label">Booking Slot</label>
              <select
                class="form-control"
                v-model="selectedBookingSlotId"
                :disabled="wingEnabled ? !selectedWing : false"
                cy="slots"
                v-on:change="clearDateSelection()">
                <optgroup v-for="(groupedSlot, index) in bookingSlotsGrouped" :key="index"
                          :label="groupedSlot.day_of_week">
                  <option v-for="slot in groupedSlot.slots" :key="slot.booking_slot_id" :value="slot.booking_slot_id">
                    {{slot.name}} {{slot.slot_begin}} - {{slot.slot_end}}
                  </option>
                </optgroup>
              </select>
            </div>
            <div class="form-group">
              <label class="control-label">Booking Date</label>
              <p class="text-danger" v-if="dateRestricted">This booking date overlaps with a restricted date period.</p>
              <select class="form-control" cy="slot-date"
                      v-model="selectedBookingSlotDate" :disabled="!selectedBookingSlotId" @change="getVisitsHad">
                <option v-for="(date, index) in bookingSlotDates" :key="index" :value="date.date"
                        :disabled="date.remaining === 0">{{date.date | formatDate}} ({{date.remaining}} Terminals Available)
                </option>
              </select>
            </div>
            <div class="row">
              <div class="col-12">
                <p>Available Fund Tokens: <b cy="fundTokens">{{fundTokens}}</b></p>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="form-group">
                  <b-form-checkbox v-model="recordVisit" switch cy="record-call">
                    Record video call
                  </b-form-checkbox>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="form-group">
                  <label class="control-label">Security Band</label>
                  <select v-model="securityBand" class="form-control" cy="security-band">
                    <option value="Off">Off</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Max">Max</option>
                  </select>
                  <small v-if="securityBand==='Off'">In call security will be turned off. Ideal of special scenarios.</small>
                  <small v-if="securityBand==='Low'">In call security will still run but at a relaxed pace. Ideal for trusted residents with children on the video call.</small>
                  <small v-if="securityBand==='Medium'">Balance of in-call experience for the visitor and in call security.</small>
                  <small v-if="securityBand==='High'">A high level of security. May reduce the in call experience for the visitor/resident.</small>
                  <small v-if="securityBand==='Max'">The highest level of security. Please note in call experience may suffer for the visitor/resident.</small>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="form-group">
                  <label class="control-label">Adult Content Setting</label>
                  <select v-model="adultContentSettings" class="form-control" cy="adult-content">
                    <option value="Off">Off</option>
                    <option value="Alert">Alert</option>
                    <option value="Pause">Pause</option>
                  </select>
                  <small v-if="adultContentSettings === 'Off'">Adult content detection will be turned off and no action taken by the system.</small>
                  <small v-if="adultContentSettings === 'Alert'">The master terminal will be informed of any adult content that is detected. It will be up to the staff member to take action.</small>
                  <small v-if="adultContentSettings === 'Pause'">The system will automatically pause the call and warn the visitor. The visitor will then have to pass through reverification.</small>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="form-group">
                  <label class="control-label">Invalid Face Action</label>
                  <select v-model="invalidFaceEventAction" class="form-control" cy="invalid-face">
                    <option value="Pause">Pause</option>
                    <option value="Alert">Alert</option>
                  </select>
                  <small v-if="invalidFaceEventAction === 'Pause'">Invalid face detection will cause the call to be paused. The primary visitor will then have to re-verify themselves.</small>
                  <small v-if="invalidFaceEventAction === 'Alert'">The master terminal will receive a notification when an invalid face is detected. Leaving it up to the staff member to pause and resume the call.</small>
                </div>
              </div>
            </div>
            <div class="row" v-if="isUserBannedOnBookingSlot()">
              <div class="col-12">
                <p class="red">
                  Unable to book for that slot as the visitor is banned on that date.
                </p>
              </div>
            </div>
            <div class="row" v-if="!loadingVisitsHadOnMonth && showVisitsHad()">
              <div class="col-12">
                <p>
                  Resident has had <b cy="call-number">{{visitsHadThisMonth}}</b> calls that month so far.
                  <span v-if="checkForAllowedVisitsPerMonth">Residents are usually allowed a limit of <b>{{allowedVisitsPerMonth}}</b> video calls a month.</span>
                </p>
              </div>
            </div>
            <div class="col-12 mt-1" :class="{'disabled': bookingVisit}">
              <div class="row">
                <b-btn variant="success"
                  :disabled="!isBookValid() || !validFundTokens() && !loadingSelectedVisitorBans"
                  v-on:click="bookAndFundVisit()"
                >
                  Book and Fund (This will use 1 of your fund tokens)
                </b-btn>
              </div>
              <div class="row mt-1" v-if="showBookButton">
                <b-btn variant="primary"
                  :disabled="!isBookValid() && !loadingSelectedVisitorBans"
                  v-on:click="bookVisit()"
                >
                  Book (This will request a payment from the visitor)
                </b-btn>
              </div>
            </div>
          </div>
        </b-card>
        <b-card v-if="selectedVisitorId">
          <!-- Bans -->
          <visitor-bans
            :editable="false"
            :visitor_user_id="selectedVisitorId"
          />
        </b-card>
      </div>
      <div class="col-sm-6" v-if="selectedVisitorId">
        <div class="row">
          <div class="col-12">
            <visitor-details :visitor_user_id="selectedVisitorId"/>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <visitors-previous-visits
              :visitor_user_id="selectedVisitorId"
              :prisoner_number="prisonerNumber"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import BookingSlotService from '@/services/BookingSlotService';
  import ContactService from '@/services/ContactService';
  import VisitService from '@/services/VisitService';
  import PrisonService from '@/services/PrisonService';
  import VisitorService from '@/services/VisitorService';
  import WingService from '@/services/WingService';
  import moment from 'moment';
  import VisitorsPreviousVisits from '@/views/visitorDetails/VisitorsPreviousVisits';
  import VisitorDetails from '@/components/visitors/details/VisitorDetails.vue';
  import AdditionalVisitorDetails from '@/components/additionalVisitors/details/AdditionalVisitorDetails';
  import { logger } from '@/util/logger';
  import VisitorBans from '../../views/bans/VisitorBans';
  import { getDateOfYearDayAndTime } from '@/util/helpers';
  import { ERROR_CODES } from '@/util/errorCodes';
  import AdditionalVisitorsService from '@/services/AdditionalVisitorsService';
  import {mapState} from 'vuex';
 
  const PARC_PRISON_ID = 'f2009ddc2bf441e1b85b2dfcd186aeeb';
 
  export default {
    name: 'book-visit',
    components: {
      VisitorDetails,
      VisitorsPreviousVisits,
      VisitorBans,
    },
    props: {
      wingEnabled: {
        type: Boolean,
        required: true
      }
    },
    computed: {
      ...mapState({
        user: state => state.auth.user,
        prison: state => state.prison.prison,
        restrictedDates: state => state.booking.restrictedDates,
        dateRestricted: state => state.booking.dateRestricted,
      }),
    },
    data() {
      return {
        fundTokens: '-',
        prisonerNumber: '',
        wings: [],
        selectedWing: null,
        loadingWings: true,
        securityBand: 'High',
        adultContentSettings: 'Pause',
        invalidFaceEventAction: 'Pause',
        contacts: [],
        visitor: null,
        selectedVisitorId: '',
        loadingContacts: true,
        bookingSlotsWithStock: [],
        bookingSlotDates: [],
        bookingSlotsGrouped: [],
        selectedBookingSlotId: null,
        selectedBookingSlotDate: null,
        recordVisit: false,
        loadingSelectedVisitorBans: false,
        selectedVisitorBans: [],
        additionalVisitors: [],
        selectedAdditionalVisitorIds: [],
        MAX_ADDITIONAL_VISITORS: 3,
        columns: [
          {
            key: 'checkbox',
            label: '',
          },
          {
            key: 'fullName',
            label: 'Name',
          },
          {
            key: 'birthDateAndAge',
            label: 'DOB',
          },
          {
            key: 'actions',
            label: 'Actions',
          },
        ],
        currentPage: 1,
        perPage: 5,
        totalRows: 0,
        checkForAllowedVisitsPerMonth: false,
        allowedVisitsPerMonth: 2,
        loadingVisitsHadOnMonth: true,
        visitsHadThisMonth: 0,
        showBookButton: true,
        bookingVisit: false,
        latestBookingDate: null,
      };
    },
    mounted() {
      this.getPrisonInfo();
      this.getFundTokens();
      if(this.wingEnabled) {
        this.fetchWings();
      } else {
        this.getBookingSlotsWithStockForWing();  // Get stock
      }
    },
    methods: {
      getPrisonInfo() {
        this.recordVisit = this.prison.default_call_recording;
        this.securityBand = this.prison.default_security_band || 'High';
        this.adultContentSettings = this.prison.adult_content_settings || 'Pause';
        this.allowedVisitsPerMonth = this.prison.allowed_visits_per_month;
        this.checkForAllowedVisitsPerMonth = this.prison.check_for_allowed_visits_per_month;
        this.showBookButton = this.prison.allow_book_button;
        this.invalidFaceEventAction = this.prison.invalid_face_event_action || 'Pause';
        this.latestBookingDate = null;
        if (this.prison.latest_booking_date) {
          this.latestBookingDate = moment(this.prison.latest_booking_date).subtract(1, 'day').toDate();
        }
      },
      getFundTokens() {
        PrisonService.getFundTokens().then(({data}) => {
          this.fundTokens = data.tokens;
        }).catch(err => {
          logger.error(`Error getting ${this.$options.filters.term('prison')} info`, err, null, 'RequestedVisitDetails:getAvailableFundTokens');
          this.$toast.error('Something went wrong getting your fund tokens', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      },
      fetchWings() {
        WingService.getWings().then(res => {
          this.wings = res.data.filter((wing) => wing.status === 'Enabled');

          this.wings = this.wings.sort((a, b) => { // sort alphabetically
            return a.name.localeCompare(b.name);
          });

          this.loadingWings = false;
        }).catch(err => {
          logger.error('Error loading wings', err, null, 'BookVisitWithWing:fetchWings');
          this.$toast.error('Something went wrong getting your fund tokens', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      },
      showVisitsHad() {
        return this.prisonerNumber && this.selectedVisitorId && this.selectedBookingSlotDate;
      },
      getVisitsHad() {
        this.$store.dispatch('booking/isDateRestricted', this.selectedBookingSlotDate);

        if(!this.showVisitsHad()) {
          return;
        }
        this.loadingVisitsHadOnMonth = true;

        VisitService.getNumberOfVisitsThisMonth(this.prisonerNumber, this.selectedBookingSlotDate).then(res => {
          this.visitsHadThisMonth = res.data.num_of_visits;
          this.loadingVisitsHadOnMonth = false;
        });
      },
      getBookingSlotsWithStockForWing() {
        let wingId = this.wingEnabled ? this.selectedWing._id : '';
        BookingSlotService.getBookingSlotsWithStockForWing(this.prison.prison_id, wingId).then(res => {
          this.bookingSlotsWithStock = res.data;
          /*
            Group days of the week together
            [{day_of_week: string, slots: [{booking_slot_id: string, slot_begin: string, slot_end: string]}
           */
          const flatten = {};
          this.bookingSlotsWithStock.forEach((bookingSlotStock) => {
            if (this.prison.prison_id === PARC_PRISON_ID && this.selectedWing) {
              const isYpWing = this.selectedWing.name.toLowerCase().includes('yp');
              const slotNameLower = (bookingSlotStock.name || '').toLowerCase();
              const isYpAvailable = slotNameLower.includes('yp');
              const isYpExclusive = slotNameLower.includes('yp only');

              // YP wings only see slots that include YP (e.g., "YP Only", "Resident and T4 and YP")
              if (isYpWing && !isYpAvailable) return;

              // Main wings don't see YP-exclusive slots, but can see mixed slots like "Resident and T4 and YP"
              if (!isYpWing && isYpExclusive) return;
            }

            if (flatten[bookingSlotStock.day_of_week] === null || flatten[bookingSlotStock.day_of_week] === undefined) {
              flatten[bookingSlotStock.day_of_week] = {day_of_week: bookingSlotStock.day_of_week, slots: []};
            }
            flatten[bookingSlotStock.day_of_week].slots.push({
              booking_slot_id: bookingSlotStock._id,
              name: bookingSlotStock.name,
              slot_begin: bookingSlotStock.slot_begin,
              slot_end: bookingSlotStock.slot_end,
            });
          });
          Object.keys(flatten).map((key) => {
            this.bookingSlotsGrouped.push(flatten[key]);
          });
          // Order the grouped slots
          this.bookingSlotsGrouped.forEach((slot) => {
            slot.slots = slot.slots.sort((bs1, bs2) => {
              // Fake year and day, as we only care about the slot_begin time
              const year = 2000;
              const day = 1;
              const bs1StartTime = getDateOfYearDayAndTime(year, day, bs1.slot_begin);
              const bs2StartTime = getDateOfYearDayAndTime(year, day, bs2.slot_begin);
              return bs1StartTime.getTime() - bs2StartTime.getTime();
            });
          });
          // Sort booking slots so they always show Mon-Sun
          const weekDaySorter = {
            'monday': 1,
            'tuesday': 2,
            'wednesday': 3,
            'thursday': 4,
            'friday': 5,
            'saturday': 6,
            'sunday': 7,
          };
          this.bookingSlotsGrouped = this.bookingSlotsGrouped.sort((bsg1, bsg2) => {
            const bs1Day = bsg1.day_of_week.toLowerCase();
            const bs2Day = bsg2.day_of_week.toLowerCase();
            return weekDaySorter[bs1Day] - weekDaySorter[bs2Day];
          });
        }).catch(err => {
          logger.error('Error getting booking slots with stock', err, null, 'BookVisitWithWing:getBookingSlotsWithStockForWing');
          this.$toast.error('Error getting booking slots', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      },
      clearDateSelection() {
        // Clear selected date
        this.selectedBookingSlotDate = null;
        this.bookingSlotDates = [];
        // Update date list
        if (this.selectedBookingSlotId) {
          let dates = [];
          this.bookingSlotsWithStock.forEach((bookingSlotWithStock) => {
            if (bookingSlotWithStock._id === this.selectedBookingSlotId) {
              dates = bookingSlotWithStock.stock;
            }
          });
          // Order dates, closest first
          dates.sort((d1, d2) => {
            return new Date(d2).getTime() - new Date(d1).getTime();
          });
          this.bookingSlotDates = dates;
        }
      },
      searchForContacts() {
        this.visitor = null;
        this.loadingContacts = true;
        this.selectedVisitorId = null;
        this.selectedAdditionalVisitorIds = [];
        this.selectedBookingSlotDate = null;
        this.selectedBookingSlotId = null;
        // Uppercase the prisoner number
        if (this.prisonerNumber && this.prisonerNumber.length > 0) {
          this.prisonerNumber = this.prisonerNumber.toUpperCase();

          ContactService.getPrisonerContacts(this.prisonerNumber).then(res => {
            if (!res.data || res.data.length < 1) {
              this.$swal('No contacts found!', `Please check the ${this.$options.filters.term('prisoner')} Number entered is correct.`, 'warning');
            } else {
              this.$swal('Great news!', `${res.data.length} ${res.data.length <= 1 ? 'contact has' : 'contacts have'} been found. Please select one from the visitor drop down menu.`, 'success');
            }
            this.contacts = res.data;
            this.loadingContacts = false;
            this.getVisitsHad();
          }).catch(err => {
            logger.warn('Error getting contacts for Prison or Hospital Number', err, {prisonerNumber: this.prisonerNumber}, 'BookVisit:searchForContacts');
            this.$swal('No contacts found', `Please check the ${this.$options.filters.term('prisoner')} Number entered is correct`, 'warning');
            this.loadingContacts = false;
          });
        } else {
          this.$toast.info(`Please enter a ${this.$options.filters.term('prisoner')} Number`);
        }
      },
      selectWing() {
        // Clear booking slot selection
        this.selectedBookingSlotDate = null;
        this.selectedBookingSlotId = null;
        // Clear currently loaded booking slots
        this.bookingSlotsGrouped = [];
        // Get stock
        this.getBookingSlotsWithStockForWing();
      },
      selectVisitor() {
        if (this.visitor && this.visitor.user && this.visitor.user.user_id) {
          this.selectedVisitorId = this.visitor.user.user_id;
          // Get the selected visitors additional visitors and bans
          this.getSelectedVisitorsAdditionalVisitors();
          this.getSelectedVisitorsBan();
        } else {
          this.selectedVisitorId = null;
          this.selectedVisitorBans = [];
          this.selectedAdditionalVisitorIds = [];
          this.loadingSelectedVisitorBans = false;
        }
      },
      getSelectedVisitorsAdditionalVisitors() {
        AdditionalVisitorsService.getAdditionalVisitors(this.selectedVisitorId).then(res => {
          if (res.data && res.data.length > 0) {
            this.additionalVisitors = res.data.map((additionalVisitor) => {
              return {
                fullName: `${additionalVisitor.firstname} ${additionalVisitor.lastname}`,
                birthDateAndAge: `${moment(additionalVisitor.dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(additionalVisitor.dob, 'years')})`,
                ...additionalVisitor
              };
            });
          } else {
            this.additionalVisitors = [];
          }
          this.totalRows = this.additionalVisitors.length;
          this.loading = false;
        }).catch(err => {
          logger.error('Error getting visitors additionalVisitors', err, {visitorUserId: this.visitor_user_id}, 'BookVisitWithWing:getSelectedVisitorsAdditionalVisitors');
          this.$toast.error('Error getting visitors additional visitors. Please check your connection and refresh the page', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      },
      getSelectedVisitorsBan() {
        this.loadingSelectedVisitorBans = true;

        VisitorService.getVisitorBans(this.selectedVisitorId).then(res => {
          this.selectedVisitorBans = res.data;
          this.loadingSelectedVisitorBans = false;
        }).catch(err => {
          logger.error('Error getting visitor users ban', err, {visitorUserId: this.selectedVisitorId}, 'BookVisitWithWing:getSelectedVisitorsBan');
          this.$toast.error('Error checking if the selected visitor is currently banned. Please refresh the page and try again', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      },
      isUserBannedOnBookingSlot() {
        if (this.selectedBookingSlotDate) {
          let banned = false;
          const slotDate = moment(this.selectedBookingSlotDate);
          this.selectedVisitorBans.forEach((ban) => {
            const banStartDate = moment(ban.start_date);
            const banEndDate = moment(ban.end_date);
            // Note: the "[]" means inclusive checks on both start and end date
            if (slotDate.isBetween(banStartDate, banEndDate, null, '[]')) {
              banned = true;
            }
          });
          return banned;
        }
        return false;
      },
      isBookValid() {
        if(this.wingEnabled && !this.selectedWing) {
          return false;
        }

        return !(!this.prisonerNumber ||
          !this.visitor ||
          !this.selectedBookingSlotId ||
          !this.selectedBookingSlotDate ||
          this.isUserBannedOnBookingSlot());
      },
      validFundTokens() {
        try {
          const fundTokens = parseInt(this.fundTokens);
          return fundTokens > 0;
        } catch (err) {
          logger.error('Error parsing fund tokens', err, {fundTokensValue: this.fundTokens}, 'BookVisit:validFundTokens');
          return false;
        }
      },
      setBookingVisit(value){
        this.bookingVisit = value;
      },
      bookAndFundVisit() {
        if(this.checkForAllowedVisitsPerMonth && this.visitsHadThisMonth >= this.allowedVisitsPerMonth) {
          this.$swal({
            title: 'Are you sure you want to book this call?',
            text: `Resident has had ${this.visitsHadThisMonth} calls that month. They are usually only allowed ${this.allowedVisitsPerMonth}. Do you want to continue?`,
            icon: 'warning',
            buttons: true,
            dangerMode: true,
          }).then((willContinue) => {
            if(willContinue) {
              this.sendVisitRequest(true);
              this.fundTokens -= 1;
            }
          });
        } else {
          this.sendVisitRequest(true);
          this.fundTokens -= 1;
        }
      },
      bookVisit() {
        if(this.checkForAllowedVisitsPerMonth && this.visitsHadThisMonth >= this.allowedVisitsPerMonth) {
          this.$swal({
            title: 'Are you sure you want to book this visit?',
            text: `Resident has had ${this.visitsHadThisMonth} visits that month. They are usually only allowed ${this.allowedVisitsPerMonth}. Do you want to continue?`,
            icon: 'warning',
            buttons: true,
            dangerMode: true,
          }).then((willContinue) => {
            if(willContinue) {
              this.sendVisitRequest(false);
            }
          });
        } else {
          this.sendVisitRequest(false);
        }
      },
      checkDuplicateAwaitingVisits(){
        return this.$store.dispatch('booking/checkOverlappingVisitsAwaiting', {
          prisonerNumber: this.prisonerNumber,
          slotId: this.selectedBookingSlotId,
          date: this.selectedBookingSlotDate
        });
      },
      sendVisitRequest(funded) {
        this.checkDuplicateAwaitingVisits().then((duplicate) => {
          if(duplicate) {
            this.$toast.error(`Cannot book visit as ${this.prisonerNumber} is already awaiting confirmation for another visit during the chosen booking slot`, {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          } else {
            if(!this.bookingVisit){
              this.setBookingVisit(true);
              let visitRequestDto = {
                visitor_id: this.selectedVisitorId,
                contact_id: this.visitor._id,
                booked_for_booking_slot_id: this.selectedBookingSlotId,
                booked_for_booking_slot_date: this.selectedBookingSlotDate,
                funded: funded,
                should_record: this.recordVisit || false,
                additional_visitors_ids: this.selectedAdditionalVisitorIds || [],
                security_band: this.securityBand,
                adult_content_settings: this.adultContentSettings,
                invalid_face_event_action: this.invalidFaceEventAction,
                metricAction: 'WebPortalNew',
              };

              if(this.wingEnabled) {
                visitRequestDto.wing_id = this.selectedWing._id;
              }

              VisitService.bookVisit(visitRequestDto).then(res => {
                this.$toast.success('Call booked', {
                  toastClassName: ['toast-std', 'success-toast'],
                });
                this.$router.push(`/calls/${res.data._id}/details`);
              }).catch(err => {
                logger.error('Error booking visit', err, {dto: visitRequestDto}, 'BookVisitWithWing:sendVisitRequest');
                if (funded) {
                  this.fundTokens += 1;
                }

                const error = err && err.response && err.response.data && err.response.data;
                const errorMsg = error ? err.response.data : '';
                let msg = 'Something went wrong booking the visit, please refresh the page and try again';

                if (errorMsg === ERROR_CODES.VISITOR.NOT_VERIFIED) {
                  msg = 'Cannot book visit as the visitor is not verified';
                } else if (errorMsg === ERROR_CODES.VISITOR.BANNED_ON_DATE) {
                  msg = 'Cannot book visit as visitor is banned on the chosen booking slot';
                } else if (errorMsg === ERROR_CODES.VISIT_STOCK.NONE_LEFT) {
                  msg = 'Cannot book visit as the chosen booking slot has no stock left'
                } else if (errorMsg === ERROR_CODES.VISIT.PRISONER_BOOKED_ON_SLOT) {
                  msg = `Cannot book visit as ${this.prisonerNumber} is already booked on another visit during the chosen booking slot`;
                } else if (errorMsg === ERROR_CODES.PRISON.NOT_ENOUGH_FUND_TOKENS) {
                  msg = 'Unable to fund visit due to a lack of fund tokens.';
                } else if (errorMsg === ERROR_CODES.ADDITIONAL_VISITOR.NOT_VERIFIED) {
                  msg = 'Unable to book with those additional visitors as one or more is not marked as verified';
                } else if(errorMsg === ERROR_CODES.WINGS.WING_NOT_PROVIDED) {
                  msg = 'Unable to book. Please select a wing.';
                } else if(errorMsg === ERROR_CODES.WINGS.WING_MUST_BE_ENABLED) {
                  msg = 'Unable to book. Please refresh your page and select a wing that is enabled.';
                } else if(errorMsg === ERROR_CODES.WINGS.NO_WING_STOCK_LEFT) {
                  msg = 'Unable to book. There is no stock left for the wing you have select. Refresh your page and choose a different wing.';
                }

                this.$toast.error(msg, {
                  toastClassName: ['toast-std', 'warning-toast'],
                });
              }).finally(() => {
                this.setBookingVisit(false);
              });
            }
          }
        });
      },
      selectAdditionalVisitor(additionalVisitor) {
        if(this.isUnder18(additionalVisitor) && !additionalVisitor.safeguarded_at_prison) {
          // Show a warning before we add them
          this.$swal({
            title: 'Are you sure?',
            text: `${additionalVisitor.firstname} ${additionalVisitor.lastname} is under 18 and has not completed the safeguarding checks. Are you sure you want to add them as an additional visitor?`,
            icon: 'warning',
            buttons: true,
          }).then((willAdd) => {
            if(willAdd) {
              this.addAdditionalVisitorToList(additionalVisitor);
            }
          });
        } else {
          this.addAdditionalVisitorToList(additionalVisitor);
        }
      },
      addAdditionalVisitorToList(additionalVisitor) {
        if (this.selectedAdditionalVisitorIds.length < this.MAX_ADDITIONAL_VISITORS) {
          this.selectedAdditionalVisitorIds.push(additionalVisitor.additional_visitor_id);
          additionalVisitor._rowVariant = 'success';
        } else {
          this.$toast.info(`Please not you can only select a maximum of ${this.MAX_ADDITIONAL_VISITORS} additional visitors`);
        }
      },
      deSelectAdditionalVisitor(additionalVisitor) {
        if (this.selectedAdditionalVisitorIds && this.selectedAdditionalVisitorIds.length > 0) {
          this.selectedAdditionalVisitorIds = this.selectedAdditionalVisitorIds.filter((additionalVisitorId) => {
            return additionalVisitorId !== additionalVisitor.additional_visitor_id;
          });
          additionalVisitor._rowVariant = '';
        }
      },
      viewDetails(additionalVisitor) {
        this.$modal.show(AdditionalVisitorDetails, {
          additional_visitor_id: additionalVisitor.additional_visitor_id,
        }, {
          height: 'auto',
          classes: 'full-screen-modal',
          scrollable: true,
        }, {
          'before-close': () => {
            this.getSelectedVisitorsAdditionalVisitors();
          },
        });
      },
      isUnder18(additionalVisitor) {
        return parseInt(moment(new Date()).diff(additionalVisitor.dob, 'years'), 10) < 18;
      }
    },
  };
</script>
